import React from "react";
import { useTranslation } from "react-i18next";
import { StyledModal, StyledH2, StyledLabel } from "@aureskonnect/react-ui";

import { ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { useSnapshot } from "valtio";

import { store as userStore } from "../../../../components/VerticalLayout/store";

import { CustomSecondaryColorButton } from "@components/Common/CustomSecondaryColorButton";
import { useFetchGlobal } from "../../../../api/hooks";
import { showSuccessToast } from "@components/Common/SuccessToast";
import ErrorToast from "@components/Common/ErrorTost";
import { CustomMainColorButton } from "@components/Common/CustomMainColorButton";
import { dissociateProject } from "./api";
import { ProjectStatus } from "./constants";

type ConfirmationDissociateProps = {
    setIsDataUpdated: (value: boolean | number) => void;
    setIsModalDissociateOpened: (value: boolean) => void;
    isModalDissociateOpened: boolean;
    selectedRows: Record<string, any>[];
};

export function ConfirmationDissociateProject({
    setIsDataUpdated,
    setIsModalDissociateOpened,
    isModalDissociateOpened,
    selectedRows,
}: ConfirmationDissociateProps): JSX.Element {
    const { t } = useTranslation();
    const { fetchGlobal } = useFetchGlobal();
    const { userID, franchiseID, shopID, oneShop, operatorID } = useSnapshot(
        userStore
    );

    function handleSaveButtonOnClickEvent() {
        const hasPublishedTemplate = selectedRows.some(
            (row: any) => (row.Statut ?? row.Status) === ProjectStatus.PUBLISHED
        );
        if (hasPublishedTemplate) {
            ErrorToast(
                `${t(
                    "Attention, failure to dissociate one or more shops with a published template."
                )!}`
            );
            setIsModalDissociateOpened(false);
            return;
        }

        dissociateProject(fetchGlobal, {
            userID: String(userID),
            franchiseID,
            operatorID,
            oneShop,
            shopID,
            selectedRows,
        })
            .then((result: any) => {
                if (result === null) return;
                if (!result.error) {
                    showSuccessToast(
                        oneShop
                            ? `${t("The project was successfully deleted.")!}`
                            : `${t(
                                  "The dissociation was successfully completed."
                              )!}`
                    );
                    setIsModalDissociateOpened(false);
                    setIsDataUpdated(true);
                } else {
                    throw Error(result.message);
                }
            })
            .catch(() => {
                ErrorToast(`${t("There's an error")}!`);
            });
    }

    return (
        <StyledModal toggle={true} isOpen={isModalDissociateOpened} centered>
            <ModalHeader
                className="text-capitalize"
                toggle={() =>
                    setIsModalDissociateOpened(!isModalDissociateOpened)
                }
            >
                <StyledH2 className="text-uppercase">
                    {oneShop ? t("Alert") : t("Unsociate shop(s)")}
                </StyledH2>
            </ModalHeader>
            <ModalBody>
                <StyledLabel className="pl-3 m-0">
                    {oneShop
                        ? t(
                              "Do you want to confirm the deletion of the selected project?"
                          )
                        : selectedRows.length > 1
                        ? t(
                              "Do you want to confirm the dissociation of the selected stores?"
                          )
                        : `${t(
                              "Do you want to confirm the dissociation of"
                          )} ''${selectedRows[0][t("Designation")]}'' ${t(
                              "from the project"
                          )}
                              ''${selectedRows[0].Project}''?`}
                </StyledLabel>
            </ModalBody>
            <ModalFooter className="border-top-0">
                <CustomSecondaryColorButton
                    outline
                    variant="light"
                    onClick={() => {
                        setIsModalDissociateOpened(!isModalDissociateOpened);
                    }}
                    rounded
                >
                    {t("Cancel")}
                </CustomSecondaryColorButton>
                <CustomMainColorButton
                    rounded
                    className="mr-2"
                    variant="primary"
                    onClick={handleSaveButtonOnClickEvent}
                >
                    {t("Validate")}
                </CustomMainColorButton>
            </ModalFooter>
        </StyledModal>
    );
}
